import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  CardBody,
  CardContainer,
  CardHeader,
} from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import { FlexViewPrimitive } from 'src/components/layout/FlexView/FlexView.styles';
import {
  ButtonAndTooltip,
  InformationBanner,
  UserInformationPanel,
} from 'src/components/molecules';
import { RemovePIIDataModal } from 'src/components/molecules/RemovePIIDataModal/RemovePIIDataModal';
import {
  EventDetailsVm,
  ReportDetailsVm,
  reportDetailsVm,
} from 'src/components/pages/Case/Case.slice';
import { DisclosureCaseByIdQuery } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { FeatureFlag, FormState } from 'src/types';
import { useTheme } from 'styled-components';
import * as Types from '../../../types';
import {
  CaseIdTitle,
  NamedOffenderListContainer,
} from '../Case/CasePageHeader/CasePageHeader.styles';
import { NewReporterDetails } from '../NewReporterDetails';
import { ReportDetailsContainer } from '../ReportDetailsContainer';
import {
  DownloadReportButton,
  DqNamedOffendersSection,
  PostSubmissionEvidence,
  ReportDynamicQuestionsContainer,
  ReportEventsContainer,
} from './components';
import {
  ButtonWithMessageCountContainer,
  MessageCountBadge,
} from './NewReportDetails.styles';

export type NewReportDetailsProps = {
  reportId: string;
  reportTestVm?: ReportDetailsVm;
  eventsTestVm?: EventDetailsVm;
  openMessages: (channel: string) => void;
  showDynamicQuestions?: boolean;
  qaData?: DisclosureCaseByIdQuery;
  isQaLoading?: boolean;
  isQaError?: boolean;
};

export const NewReportDetails = ({
  reportId,
  reportTestVm,
  eventsTestVm,
  openMessages,
  showDynamicQuestions,
  qaData,
  isQaLoading,
  isQaError,
}: NewReportDetailsProps) => {
  const theme = useTheme();
  const isVaultVariant = theme.isVaultVariant;
  const [messagingWarningOpen, setMessagingWarningOpen] = useState(true);
  const {
    report,
    reporter,
    events,
    namedOffenders,
    enableNamedOffenders,
    messagingDisabled,
    unreadMessagesCount,
    isCaseSanitized,
    permissions: {
      canViewReportMessages,
      canSendReportMessages,
      canDownloadReport,
    },
    viewerIsCaseLead,
    ...vm
  } = useSelector(reportDetailsVm({ reportId, testVm: reportTestVm }));
  const [toggleDeletePIIModal, setToggleDeletePIIModal] = useState(false);
  const dataDeletionEnabled = useIsFeatureEnabled(FeatureFlag.DATA_DELETION);
  const isLegacyMode = useSelector(
    (state: Types.State) => state.organisation?.isLegacyMode,
  );

  const canRemovePIIData =
    dataDeletionEnabled && viewerIsCaseLead && isLegacyMode;

  const messagesButtonLabel = useMemo(() => {
    if (canSendReportMessages) {
      return 'Message reporter';
    }
    return 'View messages';
  }, [canSendReportMessages]);

  if (!report) {
    return null;
  }

  return (
    <CardContainer>
      <CardHeader
        endAccessory={
          <FlexViewPrimitive $gap={2}>
            {canRemovePIIData && (
              <Button
                data-testid="remove-pii-data-modal-button"
                variant="tertiary"
                size="small"
                onClick={() => setToggleDeletePIIModal(true)}
              >
                <StyledIconInButton
                  className="uil uil-user-exclamation"
                  size="small"
                  position="left"
                />
                Sanitise data
              </Button>
            )}
            <ButtonAndTooltip
              disabled={!enableNamedOffenders && !isCaseSanitized}
              shouldUsePortal
              buttonProps={{
                variant: 'tertiary',
                size: 'small',
                children: (
                  <>
                    <StyledIconInButton
                      className="uil uil-user"
                      size="small"
                      position="left"
                    />
                    Named offender
                  </>
                ),
              }}
              tooltipProps={{
                variant: 'up',
                width: '470px',
                removePadding: true,
                children: (
                  <NamedOffenderListContainer>
                    <UserInformationPanel
                      users={
                        isCaseSanitized &&
                        namedOffenders.filter((o) => o.name).length === 0
                          ? [
                              {
                                id: 0,
                                name: 'SANITIZED',
                                description: '',
                                badgeText: '[SANITIZED]',
                              },
                            ]
                          : namedOffenders?.map((offender) => ({
                              ...offender,
                              id: parseInt(offender.id, 10),
                              description: offender.reportOrEvent,
                            }))
                      }
                      badgeWidth="160px"
                    />
                  </NamedOffenderListContainer>
                ),
              }}
            />
            {canDownloadReport && <DownloadReportButton reportId={reportId} />}
            <ButtonWithMessageCountContainer
              size="small"
              disabled={
                messagingDisabled ||
                (!canViewReportMessages && !canSendReportMessages)
              }
              onClick={() => openMessages(reportId)}
              data-testid="message-reporter-button"
            >
              <StyledIconInButton
                size="small"
                position="left"
                className="uil uil-message"
              />
              {messagesButtonLabel}
              {Number(unreadMessagesCount) > 0 && (
                <MessageCountBadge
                  variant="custom"
                  text={`${unreadMessagesCount}`}
                  color={
                    isVaultVariant
                      ? theme.newPalette.neutral.N0
                      : theme.atlas.semantic.color.surface.default
                  }
                  background-color={
                    isVaultVariant
                      ? theme.newPalette.red.R300
                      : theme.atlas.semantic.color.status.error.default
                  }
                />
              )}
            </ButtonWithMessageCountContainer>
          </FlexViewPrimitive>
        }
      >
        <CaseIdTitle data-testid={'report-readable-id'}>
          {vm.formattedReportId}
        </CaseIdTitle>
      </CardHeader>
      <CardBody>
        <FlexViewPrimitive $gap={3} $direction="column">
          {messagingDisabled && messagingWarningOpen && (
            <InformationBanner
              state={FormState.WARNING}
              title="Reporter messaging unavailable"
              description="The reporter is either unknown or unable to chat via the Vault platform."
              onCloseMessage={() => setMessagingWarningOpen(false)}
            />
          )}
          <NewReporterDetails
            reportId={reportId}
            testVm={reportTestVm}
            data-testid="reporter-details"
          />
          <ReportDetailsContainer reportId={reportId} testVm={reportTestVm} />
          {showDynamicQuestions && (
            <DqNamedOffendersSection reportId={reportId} />
          )}
          <PostSubmissionEvidence
            report={report}
            data-testid="post-submission-evidence"
          />
          {!showDynamicQuestions ? (
            <ReportEventsContainer
              report={report}
              events={events}
              testVm={eventsTestVm}
            />
          ) : (
            <ReportDynamicQuestionsContainer
              reportId={reportId}
              qaData={qaData}
              isQaLoading={isQaLoading}
              isQaError={isQaError}
            />
          )}
        </FlexViewPrimitive>
      </CardBody>
      {toggleDeletePIIModal && (
        <RemovePIIDataModal
          open={toggleDeletePIIModal}
          setOpen={setToggleDeletePIIModal}
        />
      )}
    </CardContainer>
  );
};
